import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Button, Popover, Typography } from '@mui/material';
import { HourglassEmptyOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { useShellTranslation } from './useShellTranslation';
import { useShell } from './ShellContext';
import { useTrialStatus } from './useTrialStatus';
import {
  formatTrialEnd,
  liveDaysLeft,
  trialBodyKey,
  trialChipTone,
} from './trialChipModel';

const { ink, accent, signal, surface, line, radius, shadow } = tokens;

export { TRIAL_STATUS_QUERY_KEY } from './useTrialStatus';

/** The countdown ticks this often, so the day count and tone keep up. */
const CLOCK_TICK_MS = 60_000;

/**
 * Top-bar chip for a store on a trial (MOT-702, flag `store_trial_countdown`):
 * the trial plan and the days left, amber in the last three days. Clicking it
 * says when the trial ends and what comes after, with a link to Billing for
 * owners and admins. Renders nothing when the store is not on a trial.
 */
export const TrialChip: React.FC = () => {
  const { t } = useShellTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { closeOverlay } = useShell();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const data = useTrialStatus();

  const trial = data?.trial ?? null;
  const daysLeft = trial ? liveDaysLeft(trial.endsAt, nowMs) : null;
  // A trial that ended while the page stayed open is gone, not "0 days left".
  if (!trial || daysLeft === 0) return null;

  const tone = trialChipTone(daysLeft) === 'caution' ? signal.caution : signal.info;
  const plan = trial.planName;
  const label =
    daysLeft === null
      ? t('shell.trial.chip.noEnd', { plan })
      : t('shell.trial.chip.daysLeft', { plan, count: daysLeft });
  const shortLabel = daysLeft === null ? label : t('shell.trial.chip.short', { count: daysLeft });
  const date = trial.endsAt ? formatTrialEnd(trial.endsAt, i18n.language) : '';
  const body = t(`shell.trial.${trialBodyKey(trial)}`, {
    date,
    plan,
    after: trial.afterTrialPlanName,
  });

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          closeOverlay(); // one popover at a time
          setOpen(!open);
        }}
        sx={{
          flexShrink: 0,
          height: 30,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          p: { xs: '0 9px', sm: '0 11px 0 9px' },
          border: `1px solid ${tone.border}`,
          background: tone.tint,
          borderRadius: `${radius.pill}px`,
          fontSize: '12.5px',
          fontWeight: 600,
          color: tone.ink,
          whiteSpace: 'nowrap',
          '&:hover': { background: tone === signal.caution ? signal.caution.row : accent.tintStrong },
          '& svg': { fontSize: 15 },
        }}
      >
        <HourglassEmptyOutlined aria-hidden />
        {/* Below md only the count shows, so the header still fits at 320px. */}
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
          {label}
        </Box>
        <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
          {shortLabel}
        </Box>
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': 'trial-popover-title',
            sx: {
              mt: '6px',
              width: 300,
              maxWidth: 'calc(100vw - 32px)',
              p: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              background: surface.card,
              border: `1px solid ${line.default}`,
              borderRadius: `${radius.panel}px`,
              boxShadow: shadow.menu,
            },
          },
        }}
      >
        <Typography id="trial-popover-title" sx={{ fontSize: '14.5px', fontWeight: 700, color: ink.primary }}>
          {t('shell.trial.title', { plan })}
        </Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: ink.bodyAlt }}>{body}</Typography>
        {data?.canManageBilling ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: '4px' }}>
            <Button
              variant="contained"
              onClick={() => {
                setOpen(false);
                navigate('/app/billing');
              }}
              sx={{
                height: 32,
                px: '14px',
                background: accent.main,
                color: ink.inverse,
                boxShadow: 'none',
                '&:hover': { background: accent.hover, boxShadow: 'none' },
              }}
            >
              {t('shell.trial.viewBilling')}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12.5px', lineHeight: 1.5, color: ink.muted }}>
            {t('shell.trial.askOwner')}
          </Typography>
        )}
      </Popover>
    </>
  );
};
